import React, { useState, useEffect } from "react";
import { MdAccountBalanceWallet } from "react-icons/md";
import { HiMinus } from "react-icons/hi";
import axios from "axios";
import Swal from "sweetalert2";
import ModalWrapper from "../../../../Components/Modal/ModalWrapper";
import { loadDecrypted } from "Common Usage/localStorageUtils";
import { GiCoins } from "react-icons/gi";

const BASE_URL = process.env.REACT_APP_API_URL;
const SUGGESTIONS = [100, 500, 1000, 5000];

const DeductCoinsModal = ({
  isOpen,
  onClose,
  staffId,
  staffName,
  currentBalance,
  onSuccess,
}) => {
  const [amount, setAmount] = useState("");
  const [deducting, setDeducting] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) setAmount("");
  }, [isOpen]);

  const parsed = parseFloat(amount) || 0;
  const remaining = currentBalance - parsed;
  const isValid = parsed > 0 && parsed <= currentBalance;

  const handleDeduct = async () => {
    if (!isValid) return;

    // Confirm dialog
    const confirm = await Swal.fire({
      title: "Confirm Deduction",
      html: `
        <div style="font-size:14px;color:#374151;line-height:1.8">
          Deduct <strong style="color:#c51369" >${parsed.toLocaleString(
            "en-IN"
          )} Coins </strong>
          from <strong>${staffName}</strong>'s wallet?<br/>
          <span style="font-size:12px;color:#6b7280">
            Current Coin Balance: ${currentBalance.toLocaleString("en-IN")} → 
            Remaining: ${remaining.toLocaleString("en-IN")}
          </span>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Deduct!",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#c51369",
      cancelButtonColor: "#e5e7eb",
      customClass: {
        cancelButton: "!text-gray-700",
        popup: "!rounded-2xl",
        confirmButton: "!rounded-xl",
        cancelButton: "!rounded-xl",
      },
    });

    if (!confirm.isConfirmed) return;

    setDeducting(true);
    try {
      const token = loadDecrypted("HeartConnect-Token");

      const { data } = await axios.post(
        `${BASE_URL}/staff-wallet/deduct-coins`,
        { staffId, amount: parsed },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!data.success)
        throw new Error(data.message || "Failed to deduct coin");

      await Swal.fire({
        title: "Coins Deducted! ✅",
        html: `
          <div style="font-size:14px;color:#374151;line-height:1.8">
            <strong style="color:#c51369">${parsed.toLocaleString(
              "en-IN"
            )} Coins</strong>
            deducted from <strong>${staffName}</strong>'s wallet.<br/>
            <span style="font-size:12px;color:#6b7280">
              New Coin Balance: ${
                data.data.balance?.toLocaleString("en-IN") ?? "—"
              }
            </span>
          </div>
        `,
        icon: "success",
        confirmButtonText: "Done",
        confirmButtonColor: "#c51369",
        timer: 4000,
        timerProgressBar: true,
        customClass: {
          popup: "!rounded-2xl",
          confirmButton: "!rounded-xl",
        },
      });

      onSuccess?.(data.data); // bubble updated wallet up to parent
      onClose();
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Something went wrong.";
      const status = err.response?.status;

      const isFrozen = status === 400 && msg.toLowerCase().includes("frozen");
      const isInsufficient =
        status === 400 && msg.toLowerCase().includes("insufficient");
      const isNotFound = status === 404;

      await Swal.fire({
        title: isFrozen
          ? "Wallet Frozen ❄️"
          : isInsufficient
          ? "Insufficient coins 💸"
          : isNotFound
          ? "Wallet Not Found"
          : "Deduction Failed",
        text: msg,
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#c51369",
        customClass: {
          popup: "!rounded-2xl",
          confirmButton: "!rounded-xl",
        },
      });
    } finally {
      setDeducting(false);
    }
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        {/* ── Header ── */}
        <div className="mb-5 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50">
            <GiCoins className="text-amber-500" size={22} />
          </div>
          <div>
            <p className="text-base font-bold text-gray-900">Deduct Coins</p>
            <p className="text-xs text-gray-400">
              Remove coins from staff wallet
            </p>
          </div>
        </div>

        {/* ── Staff Info Card ── */}
        <div className="mb-5 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-800">{staffName}</p>
              <p className="text-xs text-gray-400">Staff Wallet</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">Available Coin Balance</p>
              <p className="text-lg font-bold text-amber-600">
                {currentBalance?.toLocaleString("en-IN")}
              </p>
            </div>
          </div>
        </div>

        {/* ── Amount Input ── */}
        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
            Coins to Deduct
          </label>
          <div className="relative">
            <HiMinus
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={15}
            />
            <input
              type="number"
              min="1"
              max={currentBalance}
              placeholder="Enter amount…"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-9 pr-3 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-amber-400 focus:bg-white focus:ring-2 focus:ring-amber-400/10"
            />
          </div>

          {/* Live remaining preview */}
          {parsed > 0 && (
            <p
              className={`mt-1.5 text-xs font-medium ${
                remaining < 0 ? "text-red-500" : "text-gray-400"
              }`}
            >
              {remaining < 0
                ? `⚠ Exceeds coins by ${Math.abs(remaining).toLocaleString(
                    "en-IN"
                  )}`
                : `Remaining after deduction: ${remaining.toLocaleString(
                    "en-IN"
                  )} coins`}
            </p>
          )}
        </div>

        {/* ── Quick Suggestions ── */}
        <div className="mb-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Quick Select
          </p>
          <div className="flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setAmount(String(s))}
                disabled={s > currentBalance}
                className={`rounded-lg border px-3 py-1 text-xs font-semibold transition-all active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 ${
                  amount === String(s)
                    ? "border-amber-400 bg-amber-50 text-amber-600"
                    : "border-gray-200 text-gray-500 hover:border-amber-400 hover:text-amber-500"
                }`}
              >
                - {s.toLocaleString("en-IN")} coins
              </button>
            ))}
          </div>
        </div>

        {/* ── Actions ── */}
        <div className="flex gap-2">
          <button
            onClick={handleDeduct}
            disabled={!isValid || deducting}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#c51369] py-2.5 text-sm font-semibold text-white transition hover:bg-[#a81058] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {deducting ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            ) : (
              <GiCoins size={15} />
            )}
            {deducting ? "Deducting..." : "Deduct Coins"}
          </button>
          <button
            onClick={onClose}
            className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 active:scale-[0.98]"
          >
            Cancel
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default DeductCoinsModal;
